import { AriApiQueryCreatorFetch } from '../AriApiCreator';
import { prismScopes } from '../auth';
import { AgMarketValueFarm, AgMarketValueState, AssetValueByByFarmNumberResponse, AssetTypesResponse, LandTypesResponse, OperatingTypesResponse, AgMarketPropertyAggregate, PropertyValueHistoryPoint } from './models';


/**
 * Constructs the base URL for the agricultural market value API service.
 *
 * Reads `VITE_API_MGR_BASE_URL` from environment variables and appends `/ari-agmarketvalue-svc` as the service path.
 */
const url = `${import.meta.env.VITE_API_MGR_BASE_URL}/ari-agmarketvalue-svc`;
// const url = 'http://localhost:7083/api';
/**
 * Creates an RTK Query API slice for the agricultural market value backend service, configured with token-scoped access and a curated set of query endpoints for fetching land and asset value data by farm number, property ID, state code, or aggregate filters.
 */
const MarketValueApi = AriApiQueryCreatorFetch({
  scopesObj: prismScopes,
  // @ts-ignore
  serviceURL: url,
  apiOptions: {
    reducerPath: 'marketValue',
    keepUnusedDataFor: 600,
    endpoints: (builder) => ({
      getMarketValueByFarmNumbers: builder.query<AgMarketValueFarm, string>({
        //https://ari-api-manager.azure-api.net/ari-agmarketvalue-svc/MarketValue/Farm/6401
        query: (farmNumber) => `/MarketValue/Farm/${farmNumber}`
      }),
      // Deprecated: use getAssetValuesByPropertyId instead
      getAssetValuesByFarmNumbers: builder.query<AssetValueByByFarmNumberResponse, string>({
        query: (farmNumber: string) => `AssetValues/Farm/${farmNumber}`
      }),
      getMarketValueByStateCode: builder.query<AgMarketValueState, string>({
        query: (stateCode: string) => `/MarketValue/state/${stateCode}`
      }),
      getMarketValueByPropertyId: builder.query<AgMarketValueFarm, string>({
        query: (propertyId: string) => `/MarketValue/property/${propertyId}?year=2026`
      }),
      getAssetValuesByPropertyId: builder.query<AssetValueByByFarmNumberResponse, string>({
        query: (propertyId: string) => `AssetValues/property/${propertyId}`
      }),
      // New list endpoints
      getMarketValueAssetTypes: builder.query<AssetTypesResponse, void>({
        query: () => `/MarketValue/AssetTypes`
      }),
      getMarketValueLandTypes: builder.query<LandTypesResponse, void>({
        query: () => `/MarketValue/LandTypes`
      }),
      getMarketValueOperatingTypes: builder.query<OperatingTypesResponse, void>({
        query: () => `/MarketValue/OperatingTypes`
      }),
      // Aggregate endpoint
      getMarketValuesAggregate: builder.query<AgMarketPropertyAggregate[], void>({
        query: () => {
          return {
            url:`/MarketValues`,
            method:'GET',
            params: {
            // Track the current calendar year rather than a hardcoded value so
            // the dashboard always reflects the latest valuation year.
            year: String(new Date().getFullYear())
          }};
        },

      }),
      // Historical combined land + asset value per year
      getPropertyValueHistory: builder.query<
        PropertyValueHistoryPoint[],
        { propertyId: string; fromYear?: number; toYear?: number }
      >({
        query: ({ propertyId, fromYear, toYear }) => {
          const currentYear = new Date().getFullYear();
          const from = fromYear ?? currentYear - 4;
          const to = toYear ?? currentYear;
          return `/MarketValue/property/${propertyId}/history?fromYear=${from}&toYear=${to}`;
        },
      }),
    })
  }
});

/** The named export of the market-value RTK Query API slice for use with `useDispatch` or `TypedAppDispatch`. */
export const marketValueApi = MarketValueApi;

export const {
  useGetMarketValueByFarmNumbersQuery,
  useGetAssetValuesByFarmNumbersQuery,
  useGetMarketValueByPropertyIdQuery,
  useGetAssetValuesByPropertyIdQuery,
  useLazyGetMarketValueByFarmNumbersQuery,
  useGetMarketValueByStateCodeQuery,
  useLazyGetMarketValueByStateCodeQuery,
  useGetMarketValueAssetTypesQuery,
  useGetMarketValueLandTypesQuery,
  useGetMarketValueOperatingTypesQuery,
  useGetMarketValuesAggregateQuery,
  useGetPropertyValueHistoryQuery,
} = MarketValueApi.hooks;